/**
 * One members-only log entry.
 *
 * Deliberately the same shape as the public entry page — hero, the day's
 * conditions, the writing, a picture partway down, a gallery — because it
 * is the same kind of thing, written in the same editor. Only the route it
 * is fetched from differs, and that route is the one that checks who is
 * asking.
 */

import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import type { JSX } from 'preact';

import { apiGet, ApiError } from '../api.js';
import { humanDay } from './LogView.js';
import { AskCard, type Ask } from '../components/FarmFeed.js';

interface Block {
  url: string | null;
  content_type: string | null;
  alt: string | null;
  caption: string | null;
  /** How the writer cropped it for the page: shape, focus point, zoom. */
  frame?: { aspect: number; x: number; y: number; zoom: number } | null;
  size?: 'column' | 'wide';
}

interface Entry {
  title: string;
  body: string;
  published_at: string;
  entry_date: string | null;
  hero: Block | null;
  inline: Block[];
  gallery: Block[];
  /** The body with its pictures in place; see layoutEntry in the API. */
  sections?: ({ kind: 'paragraph'; text: string } | { kind: 'media'; block: Block } | { kind: 'gallery'; blocks: Block[] })[];
  moon: { name: string; illumination: number } | null;
  members_only?: boolean;
  ask?: Ask | null;
  weather: { temp_max_f: number | null; temp_min_f: number | null; rain_mm: number | null } | null;
}

function Media({ block, className }: { block: Block; className: string }): JSX.Element | null {
  if (!block.url) return null;
  const f = block.frame;
  const at = f ? `${f.x}% ${f.y}%` : '';
  const style = f ? { objectPosition: at, transformOrigin: at, ...(f.zoom > 1 ? { transform: `scale(${f.zoom})` } : {}) } : undefined;
  const media = block.content_type?.startsWith('video/') ? (
    <video class={f ? '' : className} src={block.url} controls playsInline preload="metadata" style={style} />
  ) : (
    <img class={f ? '' : className} src={block.url} alt={block.alt ?? ''} loading="lazy" style={style} />
  );
  return f ? (
    <div class={`entry-frame ${className}`} style={{ aspectRatio: String(f.aspect) }}>
      {media}
    </div>
  ) : (
    media
  );
}

export function LogEntryView({ slug }: { slug: string }): JSX.Element {
  const [entry, setEntry] = useState<Entry | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<Entry>(`/member/v1/journal/${encodeURIComponent(slug)}`)
      .then(setEntry)
      .catch((err) =>
        setError(err instanceof ApiError && err.status === 404 ? 'That entry is not here.' : 'Could not load it.'),
      );
  }, [slug]);

  if (error) {
    return (
      <main class="view centred">
        <p class="error" role="alert">{error}</p>
        <p>
          <Link href="/log">← From the farm</Link>
        </p>
      </main>
    );
  }

  if (!entry) {
    return (
      <main class="view centred">
        <p class="muted" role="status">Loading…</p>
      </main>
    );
  }

  const day = entry.entry_date ?? entry.published_at;
  const facts: string[] = [];
  if (entry.moon) facts.push(`${entry.moon.name}, ${entry.moon.illumination}% lit`);
  if (entry.weather) {
    const { temp_max_f: high, temp_min_f: low, rain_mm: rain } = entry.weather;
    if (high != null && low != null) facts.push(`High ${high}° · Low ${low}°`);
    if (rain != null) facts.push(rain > 0 ? `Rain ${(rain / 25.4).toFixed(2)}"` : 'No rain');
  }

  // Plain text from a textarea: blank lines are paragraphs, and it is never
  // treated as markup. Pictures sit among them where the API placed them; an
  // API that predates that sends no sections, so it's the writing, then them.
  const sections =
    entry.sections ?? [
      ...entry.body
        .split(/\n{2,}/)
        .map((chunk) => chunk.trim())
        .filter(Boolean)
        .map((text) => ({ kind: 'paragraph' as const, text })),
      ...entry.inline.map((block) => ({ kind: 'media' as const, block })),
      ...(entry.gallery.length ? [{ kind: 'gallery' as const, blocks: entry.gallery }] : []),
    ];

  return (
    <main class="view article-view">
      <Link href="/log" class="link">← Farm log</Link>
      <span class="ff-eyebrow">Notes from the farm{entry.members_only ? ' · Members only' : ''}</span>
      <h1>{entry.title}</h1>

      <p class="entry-meta">
        <strong>{humanDay(day)}</strong>
        {facts.map((fact) => (
          <span key={fact}> · {fact}</span>
        ))}
      </p>

      {entry.hero && <Media block={entry.hero} className="entry-hero" />}

      {sections.map((section, index) =>
        section.kind === 'paragraph' ? (
          <p key={index}>
            {section.text.split('\n').map((line, lineIndex) => (
              <>
                {lineIndex > 0 && <br />}
                {line}
              </>
            ))}
          </p>
        ) : section.kind === 'media' ? (
          <figure key={index} class={`entry-figure${section.block.size === 'wide' ? ' is-wide' : ''}`}>
            <Media block={section.block} className="entry-inline" />
            {section.block.caption && <figcaption class="card-sub">{section.block.caption}</figcaption>}
          </figure>
        ) : (
          <div key={index} class="photo-grid" style="margin-top: 1.5rem;">
            {section.blocks.map((block, i) => (
              <Media key={i} block={block} className="" />
            ))}
          </div>
        ),
      )}

      {entry.ask && <AskCard ask={entry.ask} />}

      <p style="margin-top: 2rem;">
        <Link href="/log">← From the farm</Link>
      </p>
    </main>
  );
}
